import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  TablePagination,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  RemoveCircle as CreditNoteIcon,
  LocalAtm as RecuIcon,
} from '@mui/icons-material';
import { documentsAPI } from '../services/api';
import AttestationCreationModal from '../components/AttestationCreationModal';
import ProformaCreationModal from '../components/ProformaCreationModal';
import RecuCreationModal from '../components/RecuCreationModal';
import PaymentModal from '../components/PaymentModal';
import DocumentEditModal from '../components/DocumentEditModal';
import { useAuth } from '../contexts/AuthContext';

function DocumentsPage() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [generating, setGenerating] = useState({});
  const [downloading, setDownloading] = useState({});
  const [sending, setSending] = useState({});
  const [attestationModalOpen, setAttestationModalOpen] = useState(false);
  const [proformaModalOpen, setProformaModalOpen] = useState(false);
  const [recuModalOpen, setRecuModalOpen] = useState(false);
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  
  // Sorting state
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentsAPI.getAll();
      setDocuments(response.data);
      console.log('Loaded documents:', response.data.map(d => ({ id: d.id, type: d.document_type, number: d.document_number })));
      setError(null);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (doc, action = 'download') => {
    if (!doc?.id) {
      alert('Document indisponible');
      return;
    }

    try {
      setDownloading((prev) => ({ ...prev, [doc.id]: true }));
      const response = await documentsAPI.download(doc.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      if (action === 'download') {
        const filename = doc.document_number || 'document';
        link.setAttribute('download', `${filename}.pdf`);
      } else {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Erreur lors du téléchargement du PDF';
      alert(errorMsg);
    } finally {
      setDownloading((prev) => ({ ...prev, [doc.id]: false }));
    }
  };

  const handleGeneratePDF = async (documentId) => {
    try {
      setGenerating({ ...generating, [documentId]: true });
      const response = await documentsAPI.generatePDF(documentId);
      
      if (response.data.status === 'success') {
        // Update document in state without full reload
        setDocuments(prev => prev.map(doc => 
          doc.id === documentId 
            ? { ...doc, pdf_file: response.data.pdf_url }
            : doc
        ));
      } else {
        const errorMsg = response.data.message || 'Erreur lors de la génération du PDF';
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Erreur inconnue';
      alert('Erreur lors de la génération du PDF: ' + errorMsg);
    } finally {
      setGenerating({ ...generating, [documentId]: false });
    }
  };

  const handleSendToClient = async (documentId) => {
    try {
      setSending({ ...sending, [documentId]: true });
      const response = await documentsAPI.sendToClient(documentId);
      
      if (response.data.status === 'success') {
        await loadDocuments();
        alert(response.data.message || 'Document envoyé au client!');
      } else {
        alert(response.data.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      console.error('Error sending to client:', err);
      alert(err.response?.data?.error || 'Erreur lors de l\'envoi au client');
    } finally {
      setSending({ ...sending, [documentId]: false });
    }
  };

  const handleOpenPayments = (doc) => {
    // Get invoice details from document
    if (doc.invoice_details) {
      setSelectedInvoice({
        id: doc.invoice_details.id,
        total_ttc: doc.invoice_details.total_ttc,
        document: { document_number: doc.document_number }
      });
      setPaymentModalOpen(true);
    } else {
      alert('Aucune facture associée à ce document');
    }
  };

  const handleDeleteDocument = async (doc, pin = null) => {
    // If no PIN provided and document is not draft, show confirmation
    if (!pin && doc.status !== 'draft') {
      if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${doc.document_number} ?`)) {
        return;
      }
    }
    
    try {
      // Use the delete_document endpoint with PIN if provided
      if (pin) {
        const response = await documentsAPI.deleteDocument(doc.id, { admin_edit_pin: pin });
        if (response.data.status === 'success') {
          alert(response.data.message);
          setDocuments(prev => prev.filter(d => d.id !== doc.id));
        }
      } else {
        // Regular delete for draft documents
        await documentsAPI.delete(doc.id);
        setDocuments(prev => prev.filter(d => d.id !== doc.id));
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleEditDocument = async (doc, pin) => {
    try {
      const response = await documentsAPI.editDocument(doc.id, { admin_edit_pin: pin });
      if (response.data.status === 'success') {
        // Open edit modal after PIN validation
        setEditingDocument(doc);
        setEditModalOpen(true);
      }
    } catch (err) {
      console.error('Error editing document:', err);
      alert(err.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleDuplicateDocument = async (doc) => {
    if (!window.confirm(`Dupliquer ${doc.document_number} pour créer une nouvelle version modifiable ?`)) {
      return;
    }
    
    try {
      const response = await documentsAPI.duplicate(doc.id);
      if (response.data.status === 'success') {
        alert(`Document dupliqué: ${response.data.document.document_number}`);
        loadDocuments();
      }
    } catch (err) {
      console.error('Error duplicating document:', err);
      alert(err.response?.data?.error || 'Erreur lors de la duplication');
    }
  };

  const handleCreateAvoir = async (doc) => {
    if (!window.confirm(`Créer une Facture Avoir pour annuler ${doc.document_number} ?`)) {
      return;
    }
    
    try {
      const response = await documentsAPI.createAvoir(doc.id);
      if (response.data.status === 'success') {
        alert(`Facture Avoir créée: ${response.data.document.document_number}`);
        loadDocuments();
      }
    } catch (err) {
      console.error('Error creating avoir:', err);
      alert(err.response?.data?.error || 'Erreur lors de la création de la Facture Avoir');
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'attestation':
        return 'primary';
      case 'proforma':
        return 'warning';
      case 'invoice':
        return 'success';
      case 'recu':
        return 'info';
      case 'avoir':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'pending_client':
        return 'success'; // Officiel (sent to client)
      case 'client_accepted':
        return 'warning';
      case 'validated':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft':
        return 'Brouillon';
      case 'pending_client':
        return 'Envoyé au client';
      case 'client_accepted':
        return 'Accepté par client';
      case 'validated':
        return 'Facture définitive';
      case 'rejected':
        return 'Rejeté';
      default:
        return status;
    }
  };

  const viewDocumentDetails = async (doc) => {
    try {
      setLoadingDetails(true);
      setDetailsDialogOpen(true);
      const response = await documentsAPI.get(doc.id);
      setSelectedDocument(response.data);
    } catch (err) {
      console.error('Error loading document details:', err);
      setSelectedDocument(doc);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedDocument(null);
  };

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort comparator
  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    
    // Handle nested properties
    if (orderBy === 'client_name') {
      aVal = a.client_name || '';
      bVal = b.client_name || '';
    }
    
    // Handle dates and timestamps
    if (orderBy === 'document_date' || orderBy === 'created_at') {
      aVal = new Date(aVal || 0);
      bVal = new Date(bVal || 0);
    }
    
    // Handle numbers
    if (orderBy === 'total_ttc') {
      aVal = parseFloat(a.invoice_details?.total_ttc || 0);
      bVal = parseFloat(b.invoice_details?.total_ttc || 0);
    }
    
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
  };

  const filteredDocuments = useMemo(() => {
    return documents
      .filter(doc => {
        const matchesSearch = 
          doc.document_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (doc.client_name && doc.client_name.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesType = typeFilter === 'all' || doc.document_type === typeFilter;
        
        return matchesSearch && matchesType;
      })
      .sort(getComparator(order, orderBy));
  }, [documents, searchTerm, typeFilter, order, orderBy]);

  const paginatedDocuments = filteredDocuments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 2, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Documents
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDocuments}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
          >
            Nouveau Document
          </Button>
          <Menu
            anchorEl={createMenuAnchor}
            open={Boolean(createMenuAnchor)}
            onClose={() => setCreateMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setCreateMenuAnchor(null);
                setAttestationModalOpen(true);
              }}
            >
              <DescriptionIcon sx={{ mr: 1 }} fontSize="small" />
              Créer une Attestation
            </MenuItem>
            <MenuItem
              onClick={() => {
                setCreateMenuAnchor(null);
                setProformaModalOpen(true);
              }}
            >
              <ReceiptIcon sx={{ mr: 1 }} fontSize="small" />
              Créer une Facture Proforma
            </MenuItem>
            <MenuItem
              onClick={() => {
                setCreateMenuAnchor(null);
                setRecuModalOpen(true);
              }}
            >
              <RecuIcon sx={{ mr: 1 }} fontSize="small" />
              Créer un Reçu
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par numéro ou client..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          value={
            typeFilter === 'all' ? { value: 'all', label: 'Tous' } :
            typeFilter === 'proforma' ? { value: 'proforma', label: 'Facture Proforma' } :
            typeFilter === 'invoice' ? { value: 'invoice', label: 'Facture Définitive' } :
            typeFilter === 'attestation' ? { value: 'attestation', label: 'Attestation' } :
            typeFilter === 'recu' ? { value: 'recu', label: 'Reçu' } :
            typeFilter === 'avoir' ? { value: 'avoir', label: 'Facture Avoir' } :
            { value: 'all', label: 'Tous' }
          }
          onChange={(e, newValue) => setTypeFilter(newValue?.value || 'all')}
          options={[
            { value: 'all', label: 'Tous' },
            { value: 'proforma', label: 'Facture Proforma' },
            { value: 'invoice', label: 'Facture Définitive' },
            { value: 'attestation', label: 'Attestation (PV d\'installation)' },
            { value: 'recu', label: 'Reçu' },
            { value: 'avoir', label: 'Facture Avoir' },
          ]}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={(params) => <TextField {...params} label="Type" />}
          sx={{ minWidth: 250 }}
          disableClearable
        />
      </Box>

      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 140 }}>
                <TableSortLabel
                  active={orderBy === 'document_number'}
                  direction={orderBy === 'document_number' ? order : 'asc'}
                  onClick={() => handleRequestSort('document_number')}
                >
                  <strong>N° Document</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 100 }}>
                <TableSortLabel
                  active={orderBy === 'document_type'}
                  direction={orderBy === 'document_type' ? order : 'asc'}
                  onClick={() => handleRequestSort('document_type')}
                >
                  <strong>Type</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 90 }}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  <strong>Statut</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 90 }}><strong>Paiement</strong></TableCell>
              <TableCell sx={{ minWidth: 150 }}>
                <TableSortLabel
                  active={orderBy === 'client_name'}
                  direction={orderBy === 'client_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('client_name')}
                >
                  <strong>Client</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}><strong>Origine</strong></TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <TableSortLabel
                  active={orderBy === 'total_ttc'}
                  direction={orderBy === 'total_ttc' ? order : 'asc'}
                  onClick={() => handleRequestSort('total_ttc')}
                >
                  <strong>Montant TTC</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 140 }}>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'asc'}
                  onClick={() => handleRequestSort('created_at')}
                >
                  <strong>Date de création</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 60 }}><strong>PDF</strong></TableCell>
              <TableCell sx={{ minWidth: 180 }}><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDocuments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Aucun document trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedDocuments.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {doc.document_number}
                      </Typography>
                      {doc.converted_from_proforma && (
                        <Typography variant="caption" color="text.secondary">
                          (ex: {doc.converted_from_proforma})
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.document_type_display || doc.document_type}
                      color={getTypeColor(doc.document_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(doc.status)}
                      color={getStatusColor(doc.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {doc.invoice_details ? (
                      <Chip
                        label={doc.invoice_details.payment_status_display || doc.invoice_details.payment_status}
                        color={
                          doc.invoice_details.payment_status === 'paid' ? 'success' :
                          doc.invoice_details.payment_status === 'partial' ? 'warning' : 'default'
                        }
                        size="small"
                        variant="outlined"
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell>{doc.client_name || '-'}</TableCell>
                  <TableCell>
                    {doc.origin_document_number ? (
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {doc.origin_document_number}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {doc.total_ttc ? `${parseFloat(doc.total_ttc).toLocaleString('fr-FR')} DZD` : '-'}
                  </TableCell>
                  <TableCell>
                    {doc.created_at ? (
                      <Box>
                        <Typography variant="body2">
                          {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(doc.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    ) : (
                      new Date(doc.document_date).toLocaleDateString('fr-FR')
                    )}
                  </TableCell>
                  <TableCell>
                    {doc.pdf_file ? (
                      <Tooltip title="Télécharger PDF">
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleDownloadDocument(doc, 'download')}
                            disabled={Boolean(downloading[doc.id])}
                          >
                            {downloading[doc.id] ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DownloadIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Chip label="Non généré" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                      {/* View details */}
                      <Tooltip title="Voir détails">
                        <IconButton size="small" onClick={() => viewDocumentDetails(doc)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {/* Generate PDF */}
                      <Tooltip title={doc.pdf_file ? 'Régénérer PDF' : 'Générer PDF'}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleGeneratePDF(doc.id)}
                          disabled={generating[doc.id]}
                        >
                          {generating[doc.id] ? <CircularProgress size={18} /> : <PdfIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      
                      {/* Send to client - only for draft */}
                      {doc.status === 'draft' && ['proforma', 'attestation'].includes(doc.document_type) && (
                        <Tooltip title="Envoyer au client">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleSendToClient(doc.id)}
                            disabled={sending[doc.id]}
                          >
                            {sending[doc.id] ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {/* Payment - only after client acceptance (validated or client_accepted) */}
                      {['invoice', 'proforma'].includes(doc.document_type) && 
                       doc.invoice_details && 
                       ['validated', 'client_accepted'].includes(doc.status) && (
                        <Tooltip title="Gérer les paiements">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleOpenPayments(doc)}
                          >
                            <PaymentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {/* Edit - for draft documents, or validated documents for admins */}
                      {doc.status === 'draft' || (doc.status === 'validated' && user?.is_staff) ? (
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => {
                              if (doc.status === 'validated') {
                                // For validated documents, require PIN
                                const pin = prompt('Code PIN administrateur requis:');
                                if (pin) {
                                  handleEditDocument(doc, pin);
                                }
                              } else {
                                // For draft documents, edit directly
                                setEditingDocument(doc);
                                setEditModalOpen(true);
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      
                      {/* Delete - for draft documents, or any document for admins with PIN */}
                      {doc.status === 'draft' || user?.is_staff ? (
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              if (doc.status !== 'draft' || user?.is_staff) {
                                // Require PIN for non-draft or staff deletion
                                const pin = prompt('Code PIN administrateur requis pour supprimer:');
                                if (pin) {
                                  handleDeleteDocument(doc, pin);
                                }
                              } else {
                                // For draft documents, simple confirmation
                                if (window.confirm(`Supprimer ${doc.document_number} ?`)) {
                                  handleDeleteDocument(doc);
                                }
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      
                      {/* Duplicate - for rejected documents */}
                      {doc.status === 'rejected' && (
                        <Tooltip title="Dupliquer (nouvelle version)">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleDuplicateDocument(doc)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {/* Create Avoir - only for invoices */}
                      {doc.document_type === 'invoice' && (
                        <Tooltip title="Créer une Facture Avoir">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => {
                              console.log('Creating Avoir for invoice:', doc.document_number);
                              handleCreateAvoir(doc);
                            }}
                          >
                            <CreditNoteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredDocuments.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Lignes par page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Chip
          label={`Proformas: ${documents.filter(d => d.document_type === 'proforma').length}`}
          color="warning"
          size="small"
        />
        <Chip
          label={`Factures: ${documents.filter(d => d.document_type === 'invoice').length}`}
          color="success"
          size="small"
        />
        <Chip
          label={`Attestations: ${documents.filter(d => d.document_type === 'attestation').length}`}
          color="primary"
          size="small"
        />
        <Chip
          label={`Reçus: ${documents.filter(d => d.document_type === 'recu').length}`}
          color="info"
          size="small"
        />
        <Chip
          label={`Avoirs: ${documents.filter(d => d.document_type === 'avoir').length}`}
          color="secondary"
          size="small"
        />
      </Box>

      {/* Attestation Creation Modal */}
      <AttestationCreationModal
        open={attestationModalOpen}
        onClose={() => setAttestationModalOpen(false)}
        onSuccess={loadDocuments}
      />

      {/* Proforma Creation Modal */}
      <ProformaCreationModal
        open={proformaModalOpen}
        onClose={() => setProformaModalOpen(false)}
        onSuccess={loadDocuments}
      />

      {/* Document Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={closeDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Détails du Document
          {selectedDocument && (
            <Chip
              label={getStatusLabel(selectedDocument.status)}
              color={getStatusColor(selectedDocument.status)}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedDocument ? (
            <Box>
              {/* Basic Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">N° Document</Typography>
                  <Typography variant="body1" fontWeight="bold">{selectedDocument.document_number}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body1">
                    <Chip
                      label={selectedDocument.document_type_display || selectedDocument.document_type}
                      color={getTypeColor(selectedDocument.document_type)}
                      size="small"
                    />
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Date</Typography>
                  <Typography variant="body1">
                    {new Date(selectedDocument.document_date).toLocaleDateString('fr-FR')}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Client</Typography>
                  <Typography variant="body1">
                    {selectedDocument.client_details?.company_name || selectedDocument.client_name || '-'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Workflow Status */}
              <Typography variant="h6" gutterBottom>Workflow</Typography>
              
              {/* Sent to client */}
              {selectedDocument.status !== 'draft' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Envoyé au client</strong> - Ce document est officiel et visible par le client.
                </Alert>
              )}

              {/* Client Acceptance */}
              {selectedDocument.client_accepted && (
                <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>
                  <strong>Accepté par le client</strong>
                  {selectedDocument.client_accepted_at && (
                    <Typography variant="body2">
                      Le {new Date(selectedDocument.client_accepted_at).toLocaleDateString('fr-FR')} 
                      {selectedDocument.client_accepted_by_username && ` par ${selectedDocument.client_accepted_by_username}`}
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Client Rejection */}
              {selectedDocument.status === 'rejected' && selectedDocument.client_rejection_reason && (
                <Alert severity="error" sx={{ mb: 2 }} icon={<CloseIcon />}>
                  <strong>Rejeté par le client</strong>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Raison:</strong> {selectedDocument.client_rejection_reason}
                  </Typography>
                </Alert>
              )}

              {/* Admin Rejection */}
              {selectedDocument.admin_rejection_reason && (
                <Alert severity="error" sx={{ mb: 2 }} icon={<CloseIcon />}>
                  <strong>Rejeté par l'admin</strong>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Raison:</strong> {selectedDocument.admin_rejection_reason}
                  </Typography>
                </Alert>
              )}

              {/* Admin Validation */}
              {selectedDocument.admin_validated && (
                <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>
                  <strong>Validé par l'admin</strong>
                  {selectedDocument.admin_validated_at && (
                    <Typography variant="body2">
                      Le {new Date(selectedDocument.admin_validated_at).toLocaleDateString('fr-FR')}
                      {selectedDocument.admin_validated_by_username && ` par ${selectedDocument.admin_validated_by_username}`}
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Converted to Invoice */}
              {selectedDocument.converted_to_number && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Converti en facture définitive:</strong> {selectedDocument.converted_to_number}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Invoice Details */}
              {selectedDocument.invoice_details && (
                <>
                  <Typography variant="h6" gutterBottom>Montants</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Total HT</Typography>
                      <Typography variant="body1">
                        {parseFloat(selectedDocument.invoice_details.total_ht).toLocaleString('fr-FR', {minimumFractionDigits: 2})} DZD
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">TVA</Typography>
                      <Typography variant="body1">
                        {parseFloat(selectedDocument.invoice_details.total_vat).toLocaleString('fr-FR', {minimumFractionDigits: 2})} DZD
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Total TTC</Typography>
                      <Typography variant="h6" color="primary">
                        {parseFloat(selectedDocument.invoice_details.total_ttc).toLocaleString('fr-FR', {minimumFractionDigits: 2})} DZD
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Statut paiement</Typography>
                      <Typography variant="body1">
                        <Chip
                          label={selectedDocument.invoice_details.payment_status_display || selectedDocument.invoice_details.payment_status}
                          size="small"
                          color={selectedDocument.invoice_details.payment_status === 'paid' ? 'success' : 'warning'}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Notes */}
              {selectedDocument.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Notes</Typography>
                  <Typography variant="body2">{selectedDocument.notes}</Typography>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {selectedDocument?.pdf_file && (
            <Button
              onClick={() => handleDownloadDocument(selectedDocument, 'download')}
              startIcon={<DownloadIcon />}
            >
              Télécharger PDF
            </Button>
          )}
          <Button onClick={closeDetailsDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Modal */}
      <PaymentModal
        open={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onPaymentAdded={loadDocuments}
      />

      {/* Document Edit Modal */}
      <DocumentEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingDocument(null);
        }}
        document={editingDocument}
        onSuccess={() => {
          loadDocuments();
          setEditModalOpen(false);
        }}
      />

      {/* Recu Creation Modal */}
      <RecuCreationModal
        open={recuModalOpen}
        onClose={() => setRecuModalOpen(false)}
        onSuccess={loadDocuments}
      />
    </Container>
  );
}

export default DocumentsPage;
